import { ReactElement, useMemo } from 'react';

import { AdditionalEntitlement } from '@dreryk/egabinet-prescription-frontend-api';
import { VisitDiagnosis } from '@dreryk/egabinet-visit-frontend-api';

import { useTranslate } from '@dreryk/react-common';
import { AddCircleIcon, IconSize, IconWrapper } from '@dreryk/react-components';

import { protectFeature } from '~libs.shared.system';
import { Reimbursement } from '~libs.shared.prescription';
import { isEmpty } from '~libs.typescript';
import { Reimbursement as ReimbursementItem, ReimbursementVariant } from '~libs.desktop.shared.medicine';

import { ReimbursementSelectorValue } from './ReimbursementSelector.types';
import { ReimbursementTooltipContentBuilder } from './ReimbursementTooltipContentBuilder';

type ReimbursementSelectorContentReimbursementProps = {
   readonly reimbursement: Reimbursement;
   readonly visitDiagnoses: VisitDiagnosis[];
   readonly selectedEntitlements: AdditionalEntitlement[];
   readonly onChange: (value: ReimbursementSelectorValue) => void;
   readonly value: ReimbursementSelectorValue;
};

// eslint-disable-next-line max-lines-per-function
export const ReimbursementSelectorContentReimbursement = (
   props: ReimbursementSelectorContentReimbursementProps
): ReactElement => {
   const translate = useTranslate();
   const { reimbursement } = props;
   const reimbursementSelected = props.value?.code === reimbursement.code;
   const extendedMedicineDatabaseAvailable = protectFeature('extendedMedicineDatabase');

   const icon = reimbursementSelected ? undefined : (
      <IconWrapper size={IconSize.AUTO} testId={'selectReimbursement'}>
         <AddCircleIcon />
      </IconWrapper>
   );
   const tooltipContent = useMemo(() => {
      if (isEmpty(reimbursement.reimbursementIndication)) {
         return [];
      }

      return reimbursement.reimbursementIndication
         .map((indication) =>
            new ReimbursementTooltipContentBuilder(extendedMedicineDatabaseAvailable, translate, indication)
               .withEligibleIcd10Codes()
               .withDistinctByVisitDiagnoses(props.visitDiagnoses)
               .withDistinctByChosenEntitlements(props.selectedEntitlements)
               .withStatutorilyDesignatedFreeEntitlementsAnnotation()
               .build()
         )
         .sort((a, b) => {
            return Number(b.distinct) - Number(a.distinct);
         });
   }, [
      extendedMedicineDatabaseAvailable,
      props.selectedEntitlements,
      props.visitDiagnoses,
      reimbursement.reimbursementIndication,
      translate,
   ]);

   return (
      <ReimbursementItem
         key={reimbursement.code}
         code={reimbursement.code}
         priceDescription={reimbursement.priceDescription}
         tooltipContent={tooltipContent}
         reimbursementIndications={reimbursement.reimbursementIndication}
         variant={ReimbursementVariant.STANDARD}
         onClick={props.onChange}
         icon={icon}
         active={reimbursementSelected}
         distinct={tooltipContent.some((content) => content.distinct)}
      />
   );
};
